import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { WEB_COMPONENT_CONFIGURATION_PROFILES, WEB_COMPONENT_GRID_VIEW } from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import {
  ActiveSchedules,
  Column,
  ColumnKeys,
  COLUMNS,
  FILTERS,
  Permissions,
  PREDEFINED_FILTERS_KEYS,
  Status,
  Type,
} from '../types';
import { MaintenanceWindowGridDataProvider } from '../dataProvider/MaintenanceWindowGridDataProvider';
import { ColumnType } from '../../../grid/types/common';
import {
  inPoliciesRenderer,
  lastModifiedRenderer,
  permissionsRenderer,
  scheduleDetailsRenderer,
  windowNameRenderer,
} from './CustomRenderers';
import { NewSchedulerHelper } from '../../../utils/maintenanceWindow/patchManagement/NewSchedulerHelper';
import { OldSchedulerHelper } from '../../../utils/maintenanceWindow/patchManagement/OldSchedulerHelper';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { Rights } from '@nirvana/shared/constants';

export class MaintenanceWindowGridView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>;

    // this.dom.$companySelectorDropdown = document.getElementById('#companySelectorDropdown');
    // console.log(this.dom.$companySelectorDropdown);

    this.dom.$addWindowButton = this.dom.$gridView.querySelector('#addWindow');
    this.dom.$deleteWindowButton = this.dom.$gridView.querySelector('#deleteWindow');

    // no selections yet
    this.dom.$deleteWindowButton.disabled = true;

    // TODO: call start on grid component
    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }

    this.dom.$gridFilters = this.dom.$gridView.shadowRoot.querySelector('gz-grid-filters');
    this.dom.$moreFilter = this.dom.$gridFilters.shadowRoot.querySelector('#moreFilters');

    /**
     * disable add new window button if user desn't have manage policies rights
     * manage inventory is not needed because relay servers do not require manage inventory
     */
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$addWindowButton.disabled = true;
      this.dom.$deleteWindowButton.disabled = true;
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildGridView(data: any) {
    // properties set = false
    this.dom.$gridView.propertiesSet = false;

    // set properties on grid view
    this.setGridViewProperties(this.dom.$gridView);

    this.dom.$gridView.sideMenuParent = window.document
      .querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.shadowRoot?.querySelector('.wrapper');

    // allow all more filters to be unchecked
    this.dom.$moreFilter.allowSelectNone = true;

    // properties set true when we finish setting properties
    this.dom.$gridView.propertiesSet = true;
  }

  setGridViewProperties(gridView) {
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: false,
      displayedColumns: COLUMNS,
      filters: FILTERS,
      name: '',
      pageSize: 50,
      type: ViewType.DEFAULT,
      id: 'maintenance-window-grid-view',
    };

    gridView.columnRenderers = {
      [ColumnKeys.WINDOW_NAME]: windowNameRenderer.bind(this),
      [ColumnKeys.IN_POLICIES]: inPoliciesRenderer,
      [ColumnKeys.PERMISSIONS]: permissionsRenderer,
      [ColumnKeys.SCHEDULE_DETAILS]: scheduleDetailsRenderer.bind(this),
      [ColumnKeys.LAST_MODIFIED]: lastModifiedRenderer.bind(this),
    };

    gridView.viewId = defaultViewProps.id;
    gridView.pageSize = defaultViewProps.pageSize; // items per page
    gridView.dataProvider = new MaintenanceWindowGridDataProvider(this.dependencies, this.getColumnDetails()); // data provider used by grid view to fetch items from db
    gridView.allColumnDetails = this.getColumnDetails();
    gridView.columns = defaultViewProps.displayedColumns;
    gridView.filters = defaultViewProps.filters;
    gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    gridView.isCompactViewChecked = defaultViewProps.compactView;
    gridView.hasPagination = true;
    gridView.pageNumber = 1;
    gridView.pageSizeOptions = [
      {
        title: '10',
        value: '10',
      },
      {
        title: '50',
        value: '50',
      },
      {
        title: '100',
        value: '100',
      },
    ];
    gridView.extraParams = {};
    gridView.showGlobalActions = true;
    gridView.showConfigurableArea = true;
    gridView.defaultViewProps = defaultViewProps;
    gridView.titleConfig = new GridTitleConfig(this.translations.allMaintenanceWindows);

    gridView.hasSelectableRows = true;
  }

  getColumnDetails() {
    return [
      {
        column: Column.WINDOW_NAME,
        key: ColumnKeys.WINDOW_NAME,
        columnName: this.dependencies.translateFn('Window name'),
        type: ColumnType.STRING,
        removable: true,
        editable: true,
        filterResetValue: { contains: '' },
        sortable: true,
        // {contains: ''} {startsWith: '[some value]'} {endsWith: '[some value]'}
        // {contains: ''} === missing filterResetValue. this option can work only with empty string. assumed all records for {contains: ''}
        // filterResetValue: null should be ignored
      },
      // TODO: uncomment when more types are implemented
      // {
      //   column: Column.TYPE,
      //   key: ColumnKeys.TYPE,
      //   columnName: this.dependencies.translateFn('Type'),
      //   type: ColumnType.ENUM,
      //   values: this.getTypeValues(),
      //   removable: false,
      //   editable: false,
      //   filteringDisabled: true,
      // },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Status'),
        type: ColumnType.ENUM,
        values: this.getStatusValues(),
        removable: true,
        editable: true,
        filterResetValue: this.getStatusValues(),
        sortable: true,
      },
      {
        column: Column.SCHEDULE_DETAILS,
        key: ColumnKeys.SCHEDULE_DETAILS,
        columnName: this.dependencies.translateFn('Schedule details'),
        type: ColumnType.STRING,
        removable: true,
        editable: true,
        filteringDisabled: true,
      },
      {
        column: Column.LAST_MODIFIED,
        key: ColumnKeys.LAST_MODIFIED,
        columnName: this.dependencies.translateFn('Last modified'),
        type: ColumnType.DATE,
        removable: true,
        editable: true,
        filterResetValue: { timeRange: {} },
        sortable: true,
        // EX: will reset to last 7 days
        // filterResetValue: {
        //   timeRange: {
        //     kind: TimeRangeKind.RELATIVE_TIME_RANGE,
        //     relative: RELATIVE_TIME_RANGES.LAST_7_DAYS,
        //   },
        // },
        //
        // datetime picker will reset value if filterResetValue = {timeRange: {}}.
        // filterResetValue: null should be ignored
      },
      {
        column: Column.LAST_EDITED_BY,
        key: ColumnKeys.LAST_EDITED_BY,
        columnName: this.dependencies.translateFn('Last edited by'),
        type: ColumnType.STRING,
        removable: true,
        editable: true,
        sortable: true,
      },
      {
        column: Column.PERMISSIONS,
        key: ColumnKeys.PERMISSIONS,
        columnName: this.dependencies.translateFn('Permissions'),
        type: ColumnType.ENUM,
        values: this.getPermissionsValues(),
        removable: true,
        editable: true,
        sortable: true,
        // will reset to all
        // filterResetValue: this.getPermissionsValues()
        // filterResetValue: null should be ignored
      },
      {
        column: Column.IN_POLICIES,
        key: ColumnKeys.IN_POLICIES,
        columnName: this.dependencies.translateFn('In policies'),
        type: ColumnType.STRING,
        removable: true,
        editable: true,
        filteringDisabled: true,
        sortable: true,
      },
    ];
  }

  getPermissionsValues() {
    return [
      {
        label: this.dependencies.translateFn('Shared'),
        value: Permissions.TRUE,
      },
      {
        label: this.dependencies.translateFn('Not shared'),
        value: Permissions.FALSE,
      },
    ];
  }

  getStatusValues() {
    return [
      {
        label: this.dependencies.translateFn('Used'),
        value: Status.USED,
      },
      {
        label: this.dependencies.translateFn('Unused'),
        value: Status.UNUSED,
      },
      // TODO: uncomment when CERES-25653 is implemented
      // {
      //   label: this.dependencies.translateFn('Pending'),
      //   value: Status.PENDING,
      // },
    ];
  }

  getTypeValues() {
    return [
      {
        label: this.dependencies.translateFn('Patch Management'),
        value: Type.PATCH_MANAGEMENT,
      },
    ];
  }

  buildSuccessView(data: any) {
    this.buildGridView(data);
  }

  buildErrorView(error: Error) {
    super.buildErrorView(error);
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getMaintenanceWindowsStrings();
  }

  setTranslatedStrings() {
    this.dom.$addWindowButton.innerText = this.translations.addWindow;
    this.dom.$deleteWindowButton.innerText = this.translations.deleteWindow;
  }

  private createTemplateElement(html: string): HTMLTemplateElement {
    const templateElement = document.createElement('template');
    templateElement.innerHTML = html;

    return templateElement;
  }

  editDocument(item: any) {
    this.dom.$gridView.dispatchEvent(
      new CustomEvent('editItem', {
        detail: item,
        bubbles: true,
      })
    );
  }

  computeCellText(activeSchedules: ActiveSchedules): string {
    // count active schedules
    const counter = Object.keys(activeSchedules).length;

    let cellText;

    if (counter === 0) {
      cellText = this.dependencies.translateFn('None');
    } else if (counter === 1) {
      cellText = this.dependencies.translateFn('1 schedule');
    } else {
      cellText = this.dependencies.translateFn(`{scheduleCounter} schedules`, { scheduleCounter: counter });
    }

    return cellText;
  }

  buildTooltipValue(activeSchedules: ActiveSchedules, isFallbackSchedule: boolean): string {
    const helperOldScheduler = new OldSchedulerHelper();
    const helperNewScheduler = new NewSchedulerHelper();

    helperOldScheduler.injectDependencies(this.dependencies);
    helperNewScheduler.injectDependencies(this.dependencies);

    let tooltipValue = '';

    for (const key in activeSchedules) {
      // only used to disable for/in error
      if (activeSchedules[key]) {
        if (isFallbackSchedule) {
          tooltipValue += helperOldScheduler.getShortDescription(activeSchedules[key].oldScheduler) + '. ';
        } else {
          tooltipValue += helperNewScheduler.getShortDescription(activeSchedules[key].scheduler) + '. ';
        }
      }
    }

    tooltipValue = tooltipValue.slice(0, -2);

    return tooltipValue;
  }
}
